/**
 * Replen run (Suri, 2026-10-15). One run = everything to carry from ONE bulk shelf:
 *   1. scan the bulk shelf,
 *   2. tick the lines you have physically taken (and change the quantity if the shelf had less),
 *   3. scan each pick shelf you drop at,
 *   4. "Post all" sends every line to Mintsoft, one safe move at a time, with a result per line.
 * No reports here - the check and the reports live in the admin Replen pages. Each line has its own move id (kept for the
 * browser session), so a double tap or a retry after a lost connection can never move the same stock twice.
 */
import { api, ApiError, ApiNetworkError } from '../api.js';
import { escapeHtml, newCorrelationId, qs } from '../dom.js';
import { signal } from '../feedback.js';
import { scanWithCamera } from '../camera.js';
import type { ReplenList, ReplenRun, ReplenRunLine } from '../types.js';

const IDS_KEY = 'wr.replenMoveIds';

function norm(s: string): string {
  return s.trim().toLowerCase();
}

function describeError(err: unknown): string {
  if (err instanceof ApiNetworkError) return 'Could not reach the server — check your connection.';
  if (err instanceof ApiError) return err.message;
  return String(err);
}

function loadIds(): Record<string, string> {
  try {
    return JSON.parse(sessionStorage.getItem(IDS_KEY) ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

function saveIds(ids: Record<string, string>): void {
  try {
    sessionStorage.setItem(IDS_KEY, JSON.stringify(ids));
  } catch {
    // Private mode etc.: the ids then only live as long as this screen.
  }
}

type RowStatus = 'idle' | 'sending' | 'DONE' | 'FAILED' | 'UNSURE' | 'NETWORK';

interface Row {
  line: ReplenRunLine;
  picked: boolean;
  qty: number;
  moveId: string;
  status: RowStatus;
  message: string;
}

export async function renderReplen(container: HTMLElement): Promise<void> {
  const div = document.createElement('div');
  div.className = 'screen';
  container.appendChild(div);
  await showRuns(div);
}

async function showRuns(div: HTMLElement, notice?: { kind: 'good' | 'bad' | 'warn'; text: string }): Promise<void> {
  div.innerHTML = `
    <div class="topbar"><h1>Replen</h1><div class="actions-row"><button class="ghost" id="back">Home</button><button class="ghost" id="refresh">Refresh</button></div></div>
    ${notice ? `<div class="replen-result ${notice.kind}">${escapeHtml(notice.text)}</div>` : ''}
    <div id="body"><p class="hint center">Reading Mintsoft…</p></div>`;
  qs<HTMLButtonElement>(div, '#back').addEventListener('click', () => (location.hash = '#/'));
  qs<HTMLButtonElement>(div, '#refresh').addEventListener('click', () => void showRuns(div));
  const body = qs<HTMLDivElement>(div, '#body');
  let list: ReplenList;
  try {
    list = await api.replenList();
  } catch (err) {
    body.innerHTML = `<div class="card"><p class="error-text">${escapeHtml(describeError(err))}</p><p class="hint">Tap Refresh to try again.</p></div>`;
    return;
  }
  const pending = list.pendingLookups > 0 ? `<p class="hint">Still working out ${list.pendingLookups} more shelf${list.pendingLookups === 1 ? '' : 'es'} — tap Refresh in a moment to see them.</p>` : '';
  if (list.runs.length === 0) {
    body.innerHTML = `<div class="card center"><p><strong>Nothing to replenish right now.</strong></p></div>${pending}`;
    return;
  }
  body.innerHTML = `<p class="hint">Pick a shelf to take stock from, in walking order.</p><div class="list" id="runs"></div>${pending}`;
  const holder = qs<HTMLDivElement>(body, '#runs');
  for (const run of list.runs) {
    const btn = document.createElement('button');
    btn.className = 'replen-card';
    btn.innerHTML = `<div class="shelf">${escapeHtml(run.source)}</div>
      <div class="sku">${run.lines.length} SKU${run.lines.length === 1 ? '' : 's'} · ${run.units} units</div>
      <div class="hint">${escapeHtml([...new Set(run.lines.map((l) => l.pickLocation))].slice(0, 4).join(' · '))}${new Set(run.lines.map((l) => l.pickLocation)).size > 4 ? ' …' : ''}</div>`;
    btn.addEventListener('click', () => showRun(div, run));
    holder.appendChild(btn);
  }
}

function scanBox(id: string, placeholder: string): string {
  return `<div class="scan-input-wrap"><input type="text" id="${id}" placeholder="${placeholder}" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" /><button type="button" class="ghost camera-btn" id="${id}Cam" aria-label="Scan with camera">📷</button></div>`;
}

function wireScan(div: HTMLElement, id: string, accept: (value: string) => void): void {
  const input = qs<HTMLInputElement>(div, `#${id}`);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && input.value.trim()) {
      accept(input.value);
      input.value = '';
    }
  });
  qs<HTMLButtonElement>(div, `#${id}Cam`).addEventListener('click', () => {
    void scanWithCamera().then((v) => {
      if (v) accept(v);
    });
  });
  input.focus();
}

function showRun(div: HTMLElement, run: ReplenRun): void {
  const ids = loadIds();
  const rows: Row[] = run.lines.map((line) => {
    const key = `${run.source}|${line.sku}|${line.pickLocation}|${line.quantity}`;
    if (!ids[key]) ids[key] = newCorrelationId();
    return { line, picked: false, qty: line.quantity, moveId: ids[key]!, status: 'idle', message: '' };
  });
  saveIds(ids);
  const keyOf = (r: Row) => `${run.source}|${r.line.sku}|${r.line.pickLocation}|${r.line.quantity}`;
  const scannedShelves = new Set<string>();
  let busy = false;

  const header = (title: string) => `<div class="topbar"><h1>${escapeHtml(title)}</h1><button class="ghost" id="back">Back</button></div>`;
  const backToList = () => void showRuns(div);

  // ---- 1. scan the bulk shelf --------------------------------------------------------------
  function phaseScan(note = ''): void {
    div.innerHTML = `${header(run.source)}
      <div class="card"><p><strong>${run.lines.length} SKU${run.lines.length === 1 ? '' : 's'} · ${run.units} units</strong> to take from this shelf.</p></div>
      <div class="replen-step">1. Scan the shelf ${escapeHtml(run.source)}</div>
      ${scanBox('shelf', 'Scan the bulk shelf')}
      <p class="hint error-text" id="note">${escapeHtml(note)}</p>`;
    qs<HTMLButtonElement>(div, '#back').addEventListener('click', backToList);
    wireScan(div, 'shelf', (v) => {
      if (norm(v) === norm(run.source)) {
        signal('good');
        phaseTake();
      } else {
        signal('bad');
        phaseScan(`That is "${v.trim()}", not ${run.source}. Scan the shelf shown at the top.`);
      }
    });
  }

  // ---- 2. tick what was taken --------------------------------------------------------------
  function phaseTake(): void {
    div.innerHTML = `${header(run.source + ' ✓')}
      <div class="replen-step">2. Tick what you have taken</div>
      <div class="actions-row" style="gap:0.5rem;margin-bottom:0.5rem"><button class="ghost" id="all">Tick all</button><button class="ghost" id="none">Untick all</button></div>
      <div id="lines"></div>
      <p class="hint" id="count"></p>
      <button class="primary big" id="next" disabled>Next: drop at the pick shelves</button>`;
    qs<HTMLButtonElement>(div, '#back').addEventListener('click', backToList);
    const holder = qs<HTMLDivElement>(div, '#lines');
    const nextBtn = qs<HTMLButtonElement>(div, '#next');
    const countEl = qs<HTMLParagraphElement>(div, '#count');
    const valid = (r: Row) => Number.isInteger(r.qty) && r.qty >= 1 && r.qty <= r.line.quantity;
    function refresh(): void {
      const picked = rows.filter((r) => r.picked);
      const bad = picked.some((r) => !valid(r));
      countEl.textContent = `${picked.length} ticked · ${picked.reduce((n, r) => n + (valid(r) ? r.qty : 0), 0)} units${bad ? ' — fix the quantity shown in red' : ''}`;
      nextBtn.disabled = picked.length === 0 || bad;
    }
    rows.forEach((r, i) => {
      const el = document.createElement('div');
      el.className = 'replen-line';
      el.innerHTML = `<label><input type="checkbox" ${r.picked ? 'checked' : ''} />
        <span><strong>${escapeHtml(r.line.sku)}</strong>${r.line.productName ? `<br><span class="hint">${escapeHtml(r.line.productName)}</span>` : ''}
        <br><span class="take">→ ${escapeHtml(r.line.pickLocation)}</span> <span class="hint">(has ${r.line.current} of ${r.line.size})</span></span></label>
        <input type="number" inputmode="numeric" min="1" max="${r.line.quantity}" value="${r.qty}" aria-label="Quantity" />`;
      const cb = el.querySelector<HTMLInputElement>('input[type=checkbox]')!;
      const q = el.querySelector<HTMLInputElement>('input[type=number]')!;
      cb.addEventListener('change', () => {
        r.picked = cb.checked;
        refresh();
      });
      q.addEventListener('input', () => {
        r.qty = Number(q.value);
        q.classList.toggle('bad', !valid(r));
        refresh();
      });
      holder.appendChild(el);
      void i;
    });
    qs<HTMLButtonElement>(div, '#all').addEventListener('click', () => {
      rows.forEach((r) => (r.picked = true));
      phaseTake();
    });
    qs<HTMLButtonElement>(div, '#none').addEventListener('click', () => {
      rows.forEach((r) => (r.picked = false));
      phaseTake();
    });
    nextBtn.addEventListener('click', () => {
      scannedShelves.clear();
      phaseDrop();
    });
    refresh();
  }

  // ---- 3. scan each pick shelf -------------------------------------------------------------
  function phaseDrop(note = ''): void {
    const picked = rows.filter((r) => r.picked);
    const shelves: { name: string; rows: Row[] }[] = [];
    for (const r of picked) {
      const s = shelves.find((x) => norm(x.name) === norm(r.line.pickLocation));
      if (s) s.rows.push(r);
      else shelves.push({ name: r.line.pickLocation, rows: [r] });
    }
    const all = shelves.every((s) => scannedShelves.has(norm(s.name)));
    div.innerHTML = `${header('Drop stock')}
      <div class="replen-step">3. Scan each pick shelf as you put the stock on it</div>
      <div id="shelves">${shelves
        .map(
          (s) => `<div class="replen-source${scannedShelves.has(norm(s.name)) ? ' chosen' : ''}"><span><strong>${scannedShelves.has(norm(s.name)) ? '✓ ' : ''}${escapeHtml(s.name)}</strong></span><span>${s.rows.length} SKU${s.rows.length === 1 ? '' : 's'} · ${s.rows.reduce((n, r) => n + r.qty, 0)} units</span></div>`,
        )
        .join('')}</div>
      ${all ? '' : scanBox('drop', 'Scan a pick shelf')}
      <p class="hint error-text" id="note">${escapeHtml(note)}</p>
      <button class="primary big" id="post" ${all ? '' : 'disabled'}>Post all to Mintsoft (${picked.length} line${picked.length === 1 ? '' : 's'})</button>`;
    qs<HTMLButtonElement>(div, '#back').addEventListener('click', () => phaseTake());
    if (!all) {
      wireScan(div, 'drop', (v) => {
        const hit = shelves.find((s) => norm(s.name) === norm(v));
        if (!hit) {
          signal('bad');
          phaseDrop(`"${v.trim()}" is not one of the shelves listed.`);
        } else {
          scannedShelves.add(norm(hit.name));
          signal('good');
          phaseDrop();
        }
      });
    }
    qs<HTMLButtonElement>(div, '#post').addEventListener('click', () => void phasePost());
  }

  // ---- 4. post all -------------------------------------------------------------------------
  function resultsHtml(): string {
    return rows
      .filter((r) => r.picked)
      .map((r) => {
        const icon = r.status === 'DONE' ? '✓' : r.status === 'sending' ? '…' : r.status === 'idle' ? '·' : '!';
        const cls = r.status === 'DONE' ? 'good' : r.status === 'FAILED' ? 'bad' : r.status === 'idle' || r.status === 'sending' ? '' : 'warn';
        return `<div class="replen-result-line ${cls}"><strong>${icon} ${escapeHtml(r.line.sku)}</strong> ${r.qty} → ${escapeHtml(r.line.pickLocation)}${r.message ? `<br><span class="hint">${escapeHtml(r.message)}</span>` : ''}</div>`;
      })
      .join('');
  }

  async function postRow(r: Row): Promise<boolean> {
    r.status = 'sending';
    r.message = '';
    try {
      const res = await api.replenMove({ moveId: r.moveId, sku: r.line.sku, from: run.source, to: r.line.pickLocation, quantity: r.qty });
      r.message = res.message;
      if (res.kind === 'DONE') {
        r.status = 'DONE';
      } else if (res.kind === 'FAILED') {
        r.status = 'FAILED';
        r.moveId = newCorrelationId(); // nothing moved, so a corrected try is a new move
        const ids = loadIds();
        ids[keyOf(r)] = r.moveId;
        saveIds(ids);
      } else {
        r.status = 'UNSURE'; // UNSURE / IN_PROGRESS: never offered again from here
      }
      return true;
    } catch (err) {
      // The request may or may not have arrived: keep the same move id so posting again can never move twice.
      r.status = 'NETWORK';
      r.message = `${describeError(err)} Post again — the same move will not be sent twice.`;
      return false;
    }
  }

  async function phasePost(): Promise<void> {
    if (busy) return;
    busy = true;
    const picked = rows.filter((r) => r.picked);
    const draw = (title: string, footer = '') => {
      div.innerHTML = `${header(title)}<div id="results">${resultsHtml()}</div>${footer}`;
      div.querySelector<HTMLButtonElement>('#back')?.addEventListener('click', () => {
        if (!busy) backToList();
      });
    };
    draw('Posting…');
    for (const r of picked) {
      if (r.status === 'DONE' || r.status === 'UNSURE') continue;
      const ok = await postRow(r);
      draw('Posting…');
      if (!ok) break;
    }
    busy = false;
    const done = picked.filter((r) => r.status === 'DONE').length;
    const unsure = picked.filter((r) => r.status === 'UNSURE').length;
    const retry = picked.filter((r) => r.status === 'FAILED' || r.status === 'NETWORK' || r.status === 'idle').length;
    signal(done === picked.length ? 'good' : 'warn');
    const summary = `<div class="replen-result ${done === picked.length ? 'good' : 'warn'}">${done} of ${picked.length} moved.${unsure ? ` ${unsure} not sure — check Stock Check before moving ${unsure === 1 ? 'it' : 'them'} again.` : ''}</div>`;
    draw(done === picked.length ? 'Done' : 'Result', `${summary}${retry ? '<button class="primary big" id="again">Post the rest again</button>' : ''}<button class="ghost big" id="list">Back to the list</button>`);
    div.querySelector<HTMLButtonElement>('#again')?.addEventListener('click', () => void phasePost());
    qs<HTMLButtonElement>(div, '#list').addEventListener('click', () => void showRuns(div, done === picked.length ? { kind: 'good', text: `${run.source}: ${done} line${done === 1 ? '' : 's'} moved.` } : undefined));
  }

  phaseScan();
}
